import moment from 'moment';
import { isEmpty } from 'lodash';
import {
  APP_CONFIG,
  RRP_EQUIPMENT,
  RRP_ACCESSORY,
  TERMINATE_NUMBER,
  CEASE_ADDONS,
  BUY_ADD_ONS,
  NUMBER_SHARE,
  MOBILE_SHARE,
  CHANGE_PLAN,
  REPORT_LOST,
  NUMBER_PORT,
  SER_ROADSHOW,
  NEWLINE,
  REPLACE_SIM,
  SUSPEND_LINE,
  UNSUSPEND_LINE
} from '@/config/common';
import messages from '@/config/lang/messages.json';
import { getDistanceFromLatLonInKm } from '@/helpers/mapHelpers';
import { IError, IErrors } from './models/orderModel';
import { DELIVERY_OPTIONS_CHARGES_KEYS } from '@/config/checkout';
import { checkNumberPoolingPlan, getMobileShare } from '../common';
import createStore from '../../store/index';
import { isSerDevice, isSerSim } from '../equipment';

const {
  REVIEW_ORDER_ERR_BULK_TEXT,
  REVIEW_ORDER_ERR_TEXT,
  REVIEW_ORDER_ERR_OUT_OF_STOCK_TEXT,
  REVIEW_ORDER_ERR_INSUFFICIENT_TEXT,
  REVIEW_ORDER_ERR_OUT_OF_STOCK,
  REVIEW_ORDER_ERR_INSUFFICIENT_STOCK,
  REVIEW_ORDER_ERR_SERVICE_NUMBERS_NOT_AVAILABLE,
  REVIEW_ORDER_ERR_ADD_ON_NOT_AVAILABLE,
  REVIEW_ORDER_ERR_ADD_ON_CONFLICT,
  REVIEW_ORDER_ERR_MOBILE_PLAN_NOT_AVAILABLE
} = messages;

export const getMaxLines = (orderType) => {
  const bulkConfig = APP_CONFIG[orderType]?.['bulk'];
  return bulkConfig?.max;
};

export const isBulkOrder = (orderType, noOfLines, cartDetails = {}) => {
  const bulkConfig = APP_CONFIG[orderType]?.['bulk'];
  switch (orderType) {
    case 'RRP_EQUIPMENT':
      return (
        cartDetails['equipments'] &&
        (cartDetails['equipments'].length > 1 ||
          (cartDetails['equipments'][0] && cartDetails['equipments'][0].quantity > 1))
      );
    case 'RRP_ACCESSORY':
      return cartDetails['accessories'] && cartDetails['accessories'].length > 1;
    default:
      return noOfLines >= bulkConfig?.min;
  }
};

export const getExpiryOptions = (orderType) => {
  return APP_CONFIG[orderType]?.['expiryOptions'] || [];
};

export const filterStationByPostalCode = (postalcodeitem, locationlist, distance) => {
  return locationlist.filter((station) => {
    const distanceFromLon = getDistanceFromLatLonInKm(
      postalcodeitem.lat,
      postalcodeitem.lng,
      Number(station.Latitude),
      Number(station.Longitude)
    );
    return distanceFromLon <= distance;
  });
};

export const getTCData = (data, accountType, isStaffLogin, t = (v) => v) => {
  const result = data?.termsAndConditions?.find((tc) => tc.type === accountType);

  return {
    TC: {
      title: isStaffLogin === true ? t('CHECKOUT_TC_TEXT_06') : t('CHECKOUT_TC_TEXT_02'),
      data: (result && result.value) || ''
    },
    DPP: {
      title: t('CHECKOUT_TC_TEXT_04'),
      data: data?.dataProtection
    },
    SP: {
      title: t('CHECKOUT_TC_TEXT_05'),
      data: data?.staffplantc
    }
  };
};

const getDeliveryDetail = (data) => {
  const DATE_FORMAT = 'YYYY-MM-DD';
  const deliveryDetail = {
    ...data.deliveryDetail,
    type: data?.deliveryType,
    deliveryDate: moment(data.deliveryDate, DATE_FORMAT).valueOf(),
    deliveryTimeSlotDate: data.deliveryTimeSlotDate || '',
    name: data.deliveryReceipientName,
    email: data.deliveryReceipientEmail,
    mobile: data.deliveryReceipientMobile
  };

  if (data?.deliveryType === 'POPSTATION') {
    deliveryDetail.postalCode = data.popstationDetail?.ZipCode;
    deliveryDetail.houseBlockNumber = data.popstationDetail?.HouseBlockNumber;
    deliveryDetail.buildingName = data.popstationDetail?.BuildingName;
    deliveryDetail.streetName = data.popstationDetail?.StreetName;
    deliveryDetail.POPStationName = data.popstationDetail?.POPStationName;
    deliveryDetail.unitNumber = data.popstationDetail?.UnitNumber;
  }
  if (data?.deliveryType === 'RESIDENTIAL') {
    deliveryDetail.resAddress = true;
  }
  return deliveryDetail;
};

const getPaymentDetail = (data, brn, shoppingCartType, isNumberPoolPlan = false) => {
  if (data.billingAccount && data.billingAccount.billingAddressId === 'new') {
    data.billingAccount = {
      ...data.billingAccount,
      brn,
      billingAddressId: null,
      createBillingAccount: true,
      createMultipleBillingAccount: false
    };
  }

  const billingDetail = (![NEWLINE, SER_ROADSHOW, NUMBER_PORT].includes(shoppingCartType) || isNumberPoolPlan) ? data.billingAccount : [{
    ...data.billingAccount,
    name: data.billingAttentionTo,
    attentionTo: data.billingAttentionTo,
    careOf: data.billingCareOf
  }]

  const defaultPaymentDetail = {
    paymentTypeToday: data.paymentTypeTodaysCharge,
    splitPaymentTypeToday: data.splitpaymentTypeTodaysCharge,
    paymentTypeMonthly: data.paymentTypeMonthlyCharge,
    billingAccount: billingDetail
  };

  let techFundDto = {};

  if (data.paymentTypeTodaysCharge === 'TECHFUND' && data.paymentAccountTechFund?.caseID) {
    techFundDto = {
      caseId: data.paymentAccountTechFund.caseID,
      amountUtilizedForTxn: data.paymentAccountTechFund.paymentAmount
    };
  }

  if (data.splitpaymentTypeTodaysCharge === 'TECHFUND' && data.splitPaymentAccountTechFund?.caseID) {
    techFundDto = {
      caseId: data.splitPaymentAccountTechFund.caseID,
      amountUtilizedForTxn: data.splitPaymentAccountTechFund.paymentAmount
    };
  }

  const techFundPaymentDetail = {
    ...defaultPaymentDetail,
    techFundDto: techFundDto
  };

  const paymentDetail = [data.paymentTypeTodaysCharge, data.splitpaymentTypeTodaysCharge].includes('TECHFUND') ? techFundPaymentDetail : defaultPaymentDetail;
  return paymentDetail;
};

export const getOrderRequestData = (data, currentOrder) => {
  const { shoppingCartId, brn, shoppingCartType, price } = currentOrder;
  const isNumberPoolPlan = checkNumberPoolingPlan(currentOrder?.cartDetails?.pricePlan?.pricePlanGroupId)
  const deliveryDetail = getDeliveryDetail(data);
  const paymentDetail = getPaymentDetail(data, brn, shoppingCartType, isNumberPoolPlan);
  // fix needed - code refactoring
  delete data.popstationDetail;
  delete data.deliveryTimeSlotDate;
  delete data.deliveryType;
  delete data.paymentTypeMonthlyCharge;
  delete data.paymentTypeTodaysCharge;
  delete data.billingAccount;
  delete data.paymentAccountTechFund;
  delete data.splitPaymentAccountTechFund;
  delete data.switchToPdpaFlowForResAdd;
  delete data.deliveryReceipientName;
  delete data.deliveryReceipientMobile;
  delete data.tcAgreed;
  delete data.splitpaymentTypeTodaysCharge;
  delete data.deliveryReceipientEmail;
  return {
    ...data,
    deliveryDetail,
    paymentDetail,
    shoppingCartId,
    brn,
    transactionType: shoppingCartType,
    amountTodayCalculateCharge: price?.totalTodaysCharge
  };
};

const getEffectiveDate = (currentOrder, transactionType) => {
  if ([BUY_ADD_ONS, CHANGE_PLAN, UNSUSPEND_LINE].includes(transactionType) && currentOrder.cartDetails.effectiveDate) {
    return currentOrder.cartDetails.effectiveDate;
  }
  if (transactionType === NUMBER_SHARE) {
    return moment().format('DD-MM-YYYY');
  }
  if (transactionType === SUSPEND_LINE) {
    return currentOrder.cartDetails.effectiveDate;
  }
  return undefined
}

const getReportLostWithSim = (currentOrder) => {
  return currentOrder.cartDetails?.simCard?.quantity >= 1 || false
}

export const updateOrderRequest = (reqPayload, currentOrder) => {
  if (reqPayload.firstPaymentAmountTodayCharge) {
    reqPayload.firstPaymentAmountTodayCharge = parseFloat(reqPayload.firstPaymentAmountTodayCharge);
  }
  if (reqPayload.paymentAccount) {
    if (reqPayload.customerId !== reqPayload.paymentAccount) {
      reqPayload.customerId = reqPayload.paymentAccount;
    }
  }
  if (reqPayload.transactionType === TERMINATE_NUMBER && currentOrder.cartDetails?.terminateDate) {
    reqPayload['terminateDate'] = currentOrder.cartDetails.terminateDate;
  }
  if (reqPayload.transactionType === CEASE_ADDONS && currentOrder.cartDetails?.ceaseDate) {
    reqPayload['ceaseDate'] = currentOrder.cartDetails.ceaseDate;
  }
  if (reqPayload.transactionType === REPORT_LOST) {
    reqPayload['reportLostWithSim'] = getReportLostWithSim(currentOrder);
  }
  const { store } = createStore;
  const { allDeliveryCharges = [], portInDateList = [] } = store.getState().checkout;
  if (reqPayload.transactionType === NUMBER_PORT) {
    reqPayload['numberPortId'] = portInDateList[0]?.numberPortId || ''
  }
  if (reqPayload['waiverRequested']) {
    reqPayload['deliveryChargeForOrderDto'] = {
      deliveryCharges: allDeliveryCharges[reqPayload.deliveryDetail.type],
      waiverRequested: reqPayload.waiverRequested
    }
  }
  if (reqPayload?.['waiverRequestComments'] !== undefined) {
    reqPayload['waiverRequested'] = true
  }
  if (reqPayload.transactionType === SUSPEND_LINE) {
    reqPayload['effectiveEndDate'] = currentOrder.cartDetails.effectiveEndDate;
  }
  reqPayload['effectiveDate'] = getEffectiveDate(currentOrder, reqPayload.transactionType)
  return reqPayload
}

const REVIEW_ORDER_CHECKOUT_ERROR_CODE = {
  INSUFFICIENT_DEVICE_STOCK: REVIEW_ORDER_ERR_INSUFFICIENT_STOCK,
  INSUFFICIENT_ACCESSORY_STOCK: REVIEW_ORDER_ERR_INSUFFICIENT_STOCK,
  DEVICE_OUT_OF_STOCK: REVIEW_ORDER_ERR_OUT_OF_STOCK,
  ACCESSORY_OUT_OF_STOCK: REVIEW_ORDER_ERR_OUT_OF_STOCK,
  SERVICE_NUMBER_NOT_AVAILABLE: REVIEW_ORDER_ERR_SERVICE_NUMBERS_NOT_AVAILABLE,
  SERVICE_NUMBERS_NOT_AVAILABLE: REVIEW_ORDER_ERR_SERVICE_NUMBERS_NOT_AVAILABLE,
  ADDON_NOT_AVAILABLE: REVIEW_ORDER_ERR_ADD_ON_NOT_AVAILABLE,
  ADDON_NOT_VALID: REVIEW_ORDER_ERR_ADD_ON_CONFLICT,
  PRICE_PLAN_NOT_AVAILABLE: REVIEW_ORDER_ERR_MOBILE_PLAN_NOT_AVAILABLE
};

const REVIEW_ORDER_ERROR_INFO = {
  DEVICE_OUT_OF_STOCK: REVIEW_ORDER_ERR_OUT_OF_STOCK_TEXT,
  ACCESSORY_OUT_OF_STOCK: REVIEW_ORDER_ERR_OUT_OF_STOCK_TEXT,
  INSUFFICIENT_DEVICE_STOCK: REVIEW_ORDER_ERR_INSUFFICIENT_TEXT,
  INSUFFICIENT_ACCESSORY_STOCK: REVIEW_ORDER_ERR_INSUFFICIENT_TEXT
};

export const getOnLoadErrorMessage = ({ code, message } = {} as IError) => {
  return messages[code] || message || code;
};

export const getCheckoutErrorMessage = ({ code, message } = {} as IError) => {
  return REVIEW_ORDER_CHECKOUT_ERROR_CODE[code] || message || code;
};

const getBottomErrors = (details) => {
  return details.reduce((acc, detail) => {
    const { error } = detail?.find(({ error } = {} as IErrors) => !isEmpty(error)) || {};
    if (isEmpty(error)) {
      return acc;
    }
    return acc.concat(getCheckoutErrorMessage(error));
  }, []);
};

const getBottomErrorsWithInfo = (errors) => {
  if (isEmpty(errors)) {
    return {};
  }
  return {
    info: REVIEW_ORDER_ERR_TEXT,
    errors
  };
};

const getBottomBanner = ({ addons, serviceNumbers, pricePlan }) => {
  const details = [addons, serviceNumbers, [pricePlan]];
  const errors = getBottomErrors(details);
  return getBottomErrorsWithInfo(errors);
};

const getTopErrors = (detailsWithError) => {
  return detailsWithError?.reduce((errors, { error, deviceDescription, quantity }) => {
    const errorMsg = error?.code && getCheckoutErrorMessage(error);
    if (errorMsg) {
      return errors.concat([`${errorMsg}: ${deviceDescription} x ${quantity}`]);
    }
    return errors;
  }, []);
};

const getTopBanner = ({ equipments, accessories }) => {
  const details = equipments?.concat(accessories);
  const detailsWithError = details?.filter(({ error }) => !isEmpty(error));
  if (!detailsWithError || detailsWithError.length < 1) return {};
  else if (detailsWithError.length > 1)
    return {
      info: REVIEW_ORDER_ERR_BULK_TEXT,
      errors: getTopErrors(detailsWithError)
    };
  const { error, deviceDescription, quantity } = detailsWithError[0];
  return {
    info: REVIEW_ORDER_ERROR_INFO[error.code] || REVIEW_ORDER_ERR_BULK_TEXT,
    errors: [`${deviceDescription} x ${quantity}`]
  };
};

export const groupTopBottomErrors = (data) => {
  const { cartDetails = {} } = data;
  const topErrorBanner = getTopBanner(cartDetails);
  const bottomErrorBanner = getBottomBanner(cartDetails);
  if (isEmpty(topErrorBanner) && isEmpty(bottomErrorBanner)) {
    return {};
  }
  return {
    topErrorBanner,
    bottomErrorBanner
  };
};

export const groupAllErrors = (data) => {
  const { cartDetails = {} } = data;
  const { addons, serviceNumbers, pricePlan, equipments, accessories } = cartDetails;
  const topDetails = equipments?.concat(accessories);
  const bottomDetails = [addons, serviceNumbers, [pricePlan]];
  const topErrors = getTopErrors(topDetails);
  const bottomErrors = getBottomErrors(bottomDetails);
  const allErrors = topErrors?.concat(bottomErrors);
  if (isEmpty(allErrors)) {
    return {};
  }
  return getBottomErrorsWithInfo(allErrors);
};

export const getTrimedStr = (str) => {
  return str?.trim().replace(/\s+/g, ' ');
};

export const getMaxDeliveryAddress = () => {
  return APP_CONFIG.deliveryAddress.maximum;
};

export const getAddressLine = ({ unitNumber, buildingName, houseBlockNumber, streetName, postalCode }) => {
  if (!postalCode || !streetName) return '';
  const getLineString = (value) => value ? value + ',' : ''
  const addressLine = `${getLineString(unitNumber)} ${getLineString(buildingName)} ${getLineString(houseBlockNumber)} ${getLineString(streetName)} ${postalCode}`;
  return addressLine;
};

export const checkCustomerIdDisable = (deliveryDetail, paymentTypeTodaysCharge) =>
  deliveryDetail?.customerId || paymentTypeTodaysCharge === 'TECHFUND' || paymentTypeTodaysCharge === 'CREDIT_TERM';

export const filterDisableFromWatchNums = (
  watchNumbers = [] as { number: string }[],
  disabledSvcNums = [] as string[]
) => {
  return watchNumbers.filter(({ number }) => disabledSvcNums.indexOf(number) < 0);
};

export const getDeliveryAddressCount = (deliveryAddressList) =>
  deliveryAddressList.filter(({ officeAddress }) => !officeAddress).length;

export const getAddressList = (data, isBulk) => {
  if (!isBulk) return data;
  return data.filter(({ officeAddress }) => officeAddress);
};

export const getResAddressList = (data, isBulk) => {
  if (!isBulk) return data;
  return data.filter(({ officeAddress }) => !officeAddress);
};

export const isRRPWithNoItems = (
  shoppingCartType,
  cartDetails = { equipments: {}, accessories: {}, pricePlan: { pricePlanGroupId: '' } }
) => {
  return (
    (shoppingCartType === RRP_EQUIPMENT && isEmpty(cartDetails?.equipments)) ||
    (shoppingCartType === RRP_ACCESSORY && isEmpty(cartDetails?.accessories))
  );
};

export const getDeliveryCharges = (data) => {
  const dataKey = DELIVERY_OPTIONS_CHARGES_KEYS;
  const deliveryCharges: any = {};
  data.forEach((option) => {
    if (dataKey[option.deliveryChargesType]) {
      deliveryCharges[dataKey[option.deliveryChargesType]] = option.deliveryCharges
        ? Number(option.deliveryCharges)
        : 'FREE';
    }
  });
  if (Object.keys(deliveryCharges).length !== Object.values(dataKey).length) {
    Object.values(dataKey).forEach((key) => {
      if (deliveryCharges[key] === undefined) {
        if (key === 'RESIDENTIAL') {
          deliveryCharges[key] = deliveryCharges?.NORMAL || 'FREE';
        } else {
          deliveryCharges[key] = 'FREE';
        }
      }
    });
  }
  return deliveryCharges;
};

export const getPayloadForPdpaFlow = (pdpaData, checkPdpa, auth, reqPayload) => {
  let reqOrderPayload = reqPayload;
  let resdentPayload = {
    ...pdpaData,
    deliveryDetail: {
      ...pdpaData.deliveryDetail,
      resAddressId: reqOrderPayload?.deliveryDetail?.deliveryAddressId,
      brn: reqOrderPayload?.deliveryDetail?.brn,
      postalCode: reqOrderPayload?.deliveryDetail?.postalCode,
      houseBlockNumber: reqOrderPayload?.deliveryDetail?.houseBlockNumber,
      streetName: reqOrderPayload?.deliveryDetail?.streetName,
      buildingName: reqOrderPayload?.deliveryDetail?.buildingName,
      unitNumber: reqOrderPayload?.deliveryDetail?.unitNumber,
      name: reqOrderPayload?.deliveryDetail?.recpFirstName,
      email: reqOrderPayload?.deliveryDetail?.email,
      mobile: reqOrderPayload?.deliveryDetail?.mobile,
      deliveryDate: reqOrderPayload?.deliveryDetail?.deliveryDate,
      deliveryTimeSlotDate: reqOrderPayload?.deliveryDetail?.deliveryTimeSlotDate
    },
    paymentDetail: {
      paymentTypeToday: pdpaData.paymentDetail.paymentTypeToday,
      billingAccount: [pdpaData?.paymentDetail.billingAccount]
    }
  };
  delete resdentPayload.price;
  const timeSlot =
    reqOrderPayload?.deliveryDetail?.deliveryTimeSlotDate.length !== 0
      ? reqOrderPayload?.deliveryDetail?.deliveryTimeSlotDate
      : pdpaData.deliveryDetail?.deliveryTimeSlotDate;
  if (checkPdpa && (auth.txnType === 'CC_RADD' || auth.txnType === 'RADD')) {
    reqOrderPayload = resdentPayload;
  }
  if (checkPdpa && auth.txnType === 'CC') {
    reqOrderPayload = {
      ...pdpaData,
      deliveryDetail: {
        ...pdpaData.deliveryDetail,
        deliveryDate: reqOrderPayload?.deliveryDetail?.deliveryDate,
        deliveryTimeSlotDate: timeSlot
      },
      paymentDetail: {
        paymentTypeToday: pdpaData.paymentDetail.paymentTypeToday,
        billingAccount: [pdpaData?.paymentDetail.billingAccount]
      }
    };
  }
  if (checkPdpa && reqPayload?.paymentDetail?.splitPaymentTypeToday) {
    reqOrderPayload = {
      ...resdentPayload,
      firstPaymentAmountTodayCharge: parseFloat(reqPayload.firstPaymentAmountTodayCharge),
      secondPaymentAmountTodayCharge: reqPayload.secondPaymentAmountTodayCharge,
      paymentDetail: {
        ...resdentPayload.paymentDetail,
        splitPaymentTypeToday: reqPayload.paymentDetail.splitPaymentTypeToday
      }
    };
  }
  if (pdpaData?.waiverRequestComments !== undefined) {
    reqOrderPayload = {
      ...reqOrderPayload,
      deliveryDetail: {
        ...reqOrderPayload.deliveryDetail,
        resAddress: pdpaData.deliveryDetail.resAddress
      },
    };
  }
  return reqOrderPayload;
};

export const shouldShoppingCartCall = (callFetchAllProduct, fetchProductStatus) => {
  return (callFetchAllProduct && fetchProductStatus) || !callFetchAllProduct;
};

export const getNewCartOrder = (orderDetails, pricePlanGroupId, numberPoolBillAccNo, serRoadshow: any = []) => {
  let newOrderDetails = { ...orderDetails };
  const isMobileShare = getMobileShare(pricePlanGroupId, orderDetails.cartDetails?.pricePlan)
  const isNumberPool = checkNumberPoolingPlan(pricePlanGroupId);

  if (orderDetails.shoppingCartType === NEWLINE) {
    newOrderDetails.shoppingCartType = isMobileShare ? MOBILE_SHARE : NEWLINE;
    newOrderDetails['billingAccountNo'] = isNumberPool ? numberPoolBillAccNo : undefined;
  }

  if (orderDetails.shoppingCartType === SER_ROADSHOW && APP_CONFIG.SER_ROADSHOW_HYBRID_ENABLED) {
    const serDeviceList = serRoadshow?.serDeviceListsData;
    const serSvcNoList = serRoadshow?.serSvcNoListsData;
    const serDevice = isSerDevice(orderDetails?.cartDetails, serDeviceList);
    const serSim = isSerSim(orderDetails?.cartDetails?.simCard, orderDetails?.cartDetails?.serviceNumbers, serSvcNoList);
    if (APP_CONFIG.SER_ROADSHOW_HYBRID_ENABLED) {
      newOrderDetails['serDevice'] = serDevice;
      newOrderDetails['serSim'] = serSim;
    }
  }
  return newOrderDetails
}

export const getOrderSerFlow = (orderDetails, serRoadshow) => {
  if (orderDetails?.shoppingCartType !== SER_ROADSHOW) return false;
  const serDeviceList = serRoadshow?.serDeviceListsData;
  const serSvcNoList = serRoadshow?.serSvcNoListsData;
  const serDevice = isSerDevice(orderDetails?.cartDetails, serDeviceList);
  const serSim = isSerSim(orderDetails?.cartDetails?.simCard, orderDetails?.cartDetails?.serviceNumbers, serSvcNoList);
  const isSerFlow = serDevice || serSim;
  return isSerFlow;
};

export const getHybridOrderSerFlow = (orderDetails, isSerFlow) => {
  if (!APP_CONFIG.SER_ROADSHOW_HYBRID_ENABLED) return false;
  if (orderDetails?.shoppingCartType !== SER_ROADSHOW) return false;
  return !isSerFlow;
};

export const getReplaceSim = (shoppingCartType) => {
  if (shoppingCartType === REPLACE_SIM) return true;
  return false;
};

export const getSimCardDetails = (shoppingCartType, simCard) => {
  if (![TERMINATE_NUMBER, CHANGE_PLAN, BUY_ADD_ONS].includes(shoppingCartType) && simCard && Object.keys(simCard).length !== 0) return true;
  return false;
};

export const getPricePlanDetails = (pricePlan) => {
  if (pricePlan && Object.keys(pricePlan).length !== 0 && pricePlan.pricePlanName?.length) return true;
  return false;
};

export const getTotalTodaysCharge = (detailOrder) => {
  const deliveryCharge = !isNaN(detailOrder.deliveryCharge) ? detailOrder.deliveryCharge : 0;
  const totalCharge = !isNaN(detailOrder.totalTodaysCharge)
    ? detailOrder.totalTodaysCharge + deliveryCharge
    : deliveryCharge;
  return totalCharge
};

export const getBulkOrderCart = (orderDetails) => {
  return {
    activeStep: 4,
    deleted: false,
    recalculateOnly: false,
    checkout: false,
    shoppingCartType: orderDetails.transactionType,
    msaHeaderId: orderDetails.msaHeaderId,
    userName: orderDetails.createdBy,
    email: orderDetails.createdBy,
    brn: orderDetails.brn,
    mobileNumber: orderDetails.requesterServiceNumber,
    createdDate: orderDetails.orderUpdateTime,
    updateDate: orderDetails.orderUpdateTime,
    cartDetails: orderDetails.cartDetails,
    price: orderDetails.price,
    serFlow: orderDetails.serFlow
  };
};

export const getBulkOrderReport = (orderDetails) => {
  return {
    bulkOrderItemId: orderDetails.bulkOrderItemId,
    bulkOrderId: orderDetails.bulkOrderId,
    msaHeaderId: orderDetails.msaHeaderId,
    transactionType: orderDetails.transactionType,
    brn: orderDetails.brn,
    requesterName: orderDetails.requesterName,
    requesterLastName: orderDetails.requesterLastName,
    requesterServiceNumber: orderDetails.requesterServiceNumber,
    requesterEmail: orderDetails.requesterEmail,
    deliveryType: orderDetails.deliveryDetail.type || '',
    deliveryDate: orderDetails.deliveryDetail.deliveryDate || '',
    deliveryTimeSlot: orderDetails.deliveryDetail.deliveryTimeSlotDate || '',
    deliveryDetail: orderDetails.deliveryDetail,
    paymentTypeTodaysCharge: orderDetails.paymentDetail.paymentTypeToday || '',
    paymentTypeMonthlyCharge: orderDetails.paymentDetail.paymentTypeMonthly || '',
    billingAccount: orderDetails.paymentDetail.billingAccount || [],
    customerId: orderDetails.customerId,
    price: orderDetails.price,
    orderStatus: orderDetails.orderStatus,
    orderUpdateTime: orderDetails.orderUpdateTime,
    purchaseFocalScopeId: orderDetails.purchaseFocalScopeId,
    splitpaymentTypeToday: orderDetails.paymentDetail.splitPaymentTypeToday || '',
    waiverRequested: orderDetails.waiverRequested
  };
};